import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import SetOrderDialog from './components/SetOrderDialog';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useGetSubFaqListQuery } from '@/api/information/subFaqApiSlice';
import {
	InformationQaQuestionDTO,
	SubFaqPageFilter,
} from '@/api/DTO/information/subFaq.interface';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import SubFaqListSummaryFilterSection from './components/SubFaqListSummaryFilterSection';
import { useGetFaqDetailQuery } from '@/api/information/faqApiSlice';

export interface subFaqListDataGrid {
	updatedAt: string;
	createdAt: string;
}

export default function SubFaqListSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const { faqId } = useParams();
	const navigate = useNavigate();

	const defaultPropertyFilter: SubFaqPageFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<SubFaqPageFilter> = defaultPropertyFilter;
	const methods = useForm<SubFaqPageFilter>({
		defaultValues,
	});
	const transformedSearchParams: SubFaqPageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetSubFaqListQuery(
			{ ...transformedSearchParams, overviewId: faqId },
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const { data: detailData } = useGetFaqDetailQuery(faqId || '', {
		refetchOnMountOrArgChange: true,
	});

	const transformResponse = useCallback(
		(response?: InformationQaQuestionDTO[]) => {
			if (response == null) return;
			const newResponse: subFaqListDataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as SubFaqPageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'questionZh',
			headerName: t('subFaq.questionZh'),
			flex: 15,
		},
		{
			field: 'answerZh',
			headerName: t('subFaq.answerZh'),
			flex: 25,
			renderCell: (params) => (
				<pre
					style={{
						background: 'none',
						color: 'rgba(0, 0, 0, 0.87)',
						overflowWrap: 'break-word',
						wordBreak: 'break-word',
						whiteSpace: 'normal',
						fontSize: '14px',
						padding: 0,
						lineHeight: '1.5',
					}}
				>
					{params.row.answerZh}
				</pre>
			),
		},
		{
			field: 'questionEn',
			headerName: t('subFaq.questionEn'),
			flex: 15,
		},
		{
			field: 'answerEn',
			headerName: t('subFaq.answerEn'),
			flex: 25,
			renderCell: (params) => (
				<pre
					style={{
						background: 'none',
						color: 'rgba(0, 0, 0, 0.87)',
						overflowWrap: 'break-word',
						wordBreak: 'break-word',
						whiteSpace: 'normal',
						fontSize: '14px',
						padding: 0,
						lineHeight: '1.5',
					}}
				>
					{params.row.answerEn}
				</pre>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('subFaq.updatedAt'),
			flex: 10,
		},
		{
			field: 'createdAt',
			headerName: t('subFaq.createdAt'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/information-manage/faq-list/sub-faq-list/${faqId}/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{detailData?.data.titleZh}
					{detailData?.data.titleEn && `（${detailData?.data.titleEn}）`}
				</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetOrderDialog />
					<Button
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() =>
							navigate(`/information-manage/faq-list/sub-faq-list/${faqId}/add`)
						}
					>
						{t('drawerListItems.addQuestion')}
					</Button>
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<SubFaqListSummaryFilterSection></SubFaqListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
